/**
 * Atc2Json js library
 * @module universalFormatter.js
 * @version 1.0.0
 * @author Shriram Santhanam
 * @param {Object} data - The atc file data as a Uint8Array
 * @param {String} type - The atc file data as a Uint8Array
 *
 * @return {Object} {original, filtered, annotations}
 *
 */

import { defaultFilterECG } from "./ecgFilter";
import calculateLeads from "./leadCalc";
import RhythmMorphologyGen from "./RhythmMorphology";

export const DEFAULT_BEAT_LABELS = [
    {name: "Normal", color: "rgba(78, 202, 78, 0.7)", slug: "n"},
    {name: "Atrial", color: "rgba(19, 85, 204, 0.7)", slug: "a"},
    {name: "Atrial Ectopic", color: "rgba(1, 255, 255, 0.7)", slug: "c"},
    {name: "Ventricular", color: "rgba(234, 40, 10, 0.7)", slug: "v"},
    {name: "Ventricular Escape", color: "rgba(230, 145, 56, 0.7)", slug: "e"},
    {name: "Fusion", color: "rgba(99, 34, 163, 0.7)", slug: "f"},
    {name: "Junctional", color: "rgba(255, 1, 255, 0.7)", slug: "j"},
    {name: "Paced", color: "rgba(183, 183, 183, 0.7)", slug: "p"},
    {name: "Unknown", color: "rgba(67, 67, 67, 0.7)", slug: "u"},
]
const SENTINEL_LEFT_CLICK = {name: "DEFAULT_LEFT_CLICK", color: "#8c8c8c", slug: "ddf"}

function applyMedianBeatFiducialPoints(data, annotations) {
    if (data.hasOwnProperty("legend") && data.legend.hasOwnProperty("medianBeatFiducialPoints")) {
        annotations.labels.fiducialPoints = data.legend.medianBeatFiducialPoints.map((fp) => ({
            name: fp.name,
            color: fp.color,
            slug: fp.label,
        }))
    }

    if (data.hasOwnProperty("annotations") && data.annotations.hasOwnProperty("medianBeatFiducialPoints")) {
        // Pass the on-disk shape through unchanged. `setAnnotatorState` handles
        // migration for both formats (v2 byBeatType, v1 pointLabels object).
        annotations.medianBeatFiducialPoints = data.annotations.medianBeatFiducialPoints
    }

    if (data.hasOwnProperty("annotations") && data.annotations.hasOwnProperty("medianBeats")) {
        // v3 consolidated field; `setAnnotatorState` extracts fiducial points
        // from it and prefers it over the legacy field when both are present.
        annotations.medianBeats = data.annotations.medianBeats
    }
}

export default function FnWithPerformance (data, type) {
    const start = performance.now()
    const returnVal = universalFormatter(data, type)
    const end = performance.now()
    window.printPerformance("Formatting the ECG took:",`${(end-start).toFixed(2)}ms`);
    return returnVal
}

// Sniff the recording payload to pick the correct universalFormatter branch.
// The stored `type` field on a recording is written at upload time, and older
// uploads mis-stamped 1/6-lead ("turk"-shape) JSONs as `type: "json"` — which
// then routes into the 12-lead branch that requires `rawEcg` and errors out.
// Prefer the stored type but override `json` -> `turk` when the payload has
// the turk shape (`samples` + no `rawEcg`), fixing legacy recordings on read
// without a DB migration.
//
// Callers on the read path should use this instead of passing the stored type
// through verbatim. New uploads should also stamp the correct type at write
// time so this fallback isn't needed.
export const resolveEcgType = (data, storedType) => {
    if (!data || typeof data !== "object") return storedType
    if (storedType === "atc") return "atc"
    const hasRawEcg = Object.prototype.hasOwnProperty.call(data, "rawEcg")
    const hasSamples = Object.prototype.hasOwnProperty.call(data, "samples")
    // ATC-parsed-to-JSON shape: `atc2json.parse` output carries ECG data under
    // `ecg.Data` / `ecg2.Data`. Task-download re-serializes this shape as JSON
    // (e.g. `foobar.atc.json`), and on re-upload the file loses its `type`
    // stamp. Detect the shape so the ATC reader branch is used regardless.
    const hasAtcShape = Object.prototype.hasOwnProperty.call(data, "ecg")
        && data.ecg && typeof data.ecg === "object"
        && Array.isArray(data.ecg.Data)
    if (hasAtcShape) return "atc"
    if (hasRawEcg) return "json"
    if (hasSamples) return "turk"
    return storedType || "turk"
}

function universalFormatter(data, type) {
    let original = {
        frequency: 0,
        mainsFrequency: 0,
        gain: 0,
        duration: 0,
        samples: {},
        info: {},
    }

    let filtered = {
        frequency: 0,
        mainsFrequency: 0,
        gain: 0,
        duration: 0,
        samples: {},
        filters: {
            adaptive: false,
            ButterworthLowPass: 0,
            ButterworthHighPass: 0,
        },
        info: {},
    }

    let annotations = {
        beats: [], // [offset, beatType]
        intervals: [],
        doctorNote: "",
        global: [],
        medianBeatFiducialPoints: [], // [{ type: "p_onset", offsetMs: -40 }, ...] (offset from R-peak)
        labels: {
            beats: [...DEFAULT_BEAT_LABELS],
            intervals: [{id: 0 , name: "Noise", color: "#00ff00", slug: "z"}],
            fiducialPoints: [],
        },
        algDet: [],
        algStatement: { morphology: [], rhythm:[], type: ""},
        overreadMode: null,
        NeedFollowup: null,
        originalDx: null,
        confirmedDx: null,
        audit: [],
    }

    let error = null

	switch (type) {
		case "atc":
            // Check if the JSON is a valid ATC JSON
            if (!data.hasOwnProperty("ecg")) {
                return {
                    original,
                    filtered,
                    annotations,
                    error: "Uploaded ATC Not Valid",
                }
            }
            // Assemble the measured leads for use below
            const measuredLeads = data.hasOwnProperty("ecg2")
                ? { I: data.ecg.Data, II: data.ecg2.Data }  // 6L
                : { I: data.ecg.Data }                       // 1L

            // Read ECG Graph meta from ATC JSON
            original.frequency = data.fmt.Fs
            original.mainsFrequency = data.fmt.Flags[1] === 1 ? 60 : 50
            original.gain = 1e6 / data.fmt.AmpRes_nV
            original.duration = data.ecg.Data.length / data.fmt.Fs
            original.info = data.info

            filtered.frequency = original.frequency
            filtered.mainsFrequency = original.mainsFrequency
            filtered.gain = original.gain
            filtered.duration = original.duration
            filtered.info = original.info

            // Check if the ATC JSON is Raw ECG or Enhanced ECG
            if(!data.fmt.Flags[6]) {
                // Raw ECG: store measured leads as the re-filterable baseline,
                // then filter and derive all leads for display.
                // original.samples uses plain arrays (not math.js DenseMatrix)
                // so that Goertzel pre-seeding works correctly in OnFiltered.
                original.samples = measuredLeads;
                const filteredMeasured = defaultFilterECG(original.frequency, original.mainsFrequency, original.samples, original.gain);
                filtered.samples = calculateLeads(filteredMeasured);
            } else {
                // Enhanced ECG: data is pre-processed on-device; leave
                // original.samples empty so filters are disabled in the toolbar.
                filtered.samples = calculateLeads(measuredLeads);
            }
            // Set default filters
            filtered.filters.ButterworthHighPass = 0.5
            filtered.filters.ButterworthLowPass = 40
            filtered.filters.adaptive = true

            // Check if the ATC JSON has annotations
            if (data.hasOwnProperty("ann")) {
                annotations.labels.beats = [...DEFAULT_BEAT_LABELS, SENTINEL_LEFT_CLICK]
                data.ann.Data.map(beat => {
                    let type = "u"
                    switch (beat[1]) {
                        case 0:
                            type = "u"
                            break;
                        case 1:
                            type = "n"
                            break;
                        case 2:
                            type = "v"
                            break;
                        default:
                            type = "u"
                            break;
                    }
                    annotations.beats.push({i: beat[0], t:type})
                })
            }

            annotations.audit.push({time: new Date().toISOString(),person: "system",message: "File opened"})

            // CHECK IF ITS A TURK JSON
            if (data.hasOwnProperty("annotations") ) {
                if (data.annotations.hasOwnProperty("beatsV2"))
                    annotations.beats = data.annotations.beatsV2
                if (data.annotations.hasOwnProperty("intervalsV2"))
                    annotations.intervals = data.annotations.intervalsV2
                annotations.audit = data.annotator


                if (data.annotations.hasOwnProperty("global"))
                    annotations.global = data.annotations.global
                if (data.annotations.hasOwnProperty("labels")) {
                    if (data.annotations.labels.hasOwnProperty("beats")) {
                        annotations.labels.beats = data.annotations.labels.beats
                    }
                    if (data.annotations.labels.hasOwnProperty("intervals")) {
                        annotations.labels.intervals = data.annotations.labels.intervals
                    }
                }
                if (data.annotations.hasOwnProperty("doctorNote")) {
                    annotations.doctorNote = data.annotations.doctorNote
                }

                applyMedianBeatFiducialPoints(data, annotations)
            }
            return {
                original,
                filtered,
                annotations,
                error,
            }
		case "json":
            // Check if the JSON is a valid 12L ECG — accept either rawEcg or enhEcg
            if (!data.hasOwnProperty("rawEcg") && !data.hasOwnProperty("enhEcg") && !data.hasOwnProperty("filteredEcg")) {
                return {
                    original,
                    filtered,
                    annotations,
                    error: "Uploaded JSON is not a valid 12L ECG",
                }
            }
            // Use whichever ECG block is present for metadata
            const ecgMeta = data.rawEcg ?? data.filteredEcg ?? data.enhEcg;
            // Read ECG Graph meta from JSON
            original.frequency = ecgMeta.samplesPerSecond
            original.mainsFrequency = data.mainsFreq
            original.gain = 1000
            
            // Store raw measured leads for software re-filtering via the toolbar.
            // original.filteredSamples holds the on-device filtered lead set (if present)
            // so the toolbar can toggle between the two without re-fetching.
            if (data.hasOwnProperty('rawEcg')) {
                original.samples = data.rawEcg.ecgSamples;
            }
            if (data.hasOwnProperty('filteredEcg')) {
                // filteredEcg already has all derived leads pre-computed on-device.
                // Run through calculateLeads to enforce canonical lead order so the
                // grid view renders leads in the correct column/row positions.
                original.filteredSamples = calculateLeads(data.filteredEcg.ecgSamples);
                filtered.frequency = data.filteredEcg.samplesPerSecond;
            } else if (data.hasOwnProperty('enhEcg')) {
                // Fallback: older JSON format uses enhEcg instead of filteredEcg.
                original.filteredSamples = calculateLeads(data.enhEcg.ecgSamples);
                filtered.frequency = data.enhEcg.samplesPerSecond;
            }

            // Default display: on-device filtered signal if available, otherwise
            // apply software filters to the raw signal.
            if (original.filteredSamples) {
                // Show the on-device filtered signal as-is — no additional filtering.
                filtered.samples = original.filteredSamples;
                filtered.filters.viewMode = "filtered"
                // Pre-set software filter defaults so that switching to raw mode
                // immediately applies a baseline filter (removing DC offset) and
                // the standard bandpass without the user having to manually set them.
                filtered.filters.ButterworthHighPass = 0.1
                filtered.filters.ButterworthLowPass = 0
                filtered.filters.adaptive = false
            } else if (original.samples) {
                // No on-device filtered signal — apply default software filters to raw.
                const filteredMeasured = defaultFilterECG(original.frequency, original.mainsFrequency, original.samples, original.gain);
                filtered.samples = calculateLeads(filteredMeasured);
                filtered.filters.ButterworthHighPass = 0.5
                filtered.filters.ButterworthLowPass = 40
                filtered.filters.adaptive = true
                filtered.filters.viewMode = "raw"
            }

            // Set filtered ECG Graph meta from JSON
            filtered.frequency = filtered.frequency ? filtered.frequency : original.frequency
            filtered.gain = original.gain
            filtered.mainsFrequency = original.mainsFrequency
            original.duration = ecgMeta.ecgSamples.I.length / ecgMeta.samplesPerSecond
            filtered.duration = original.duration


            // Read ECG Info from JSON
            original.info = {
                ...data.ecgDeviceInfo,
                ...data.platformInfo,
                mode: data.recordMode,
            }
            filtered.info = original.info

            // Check if the JSON has annotations
            annotations.labels.beats = [...DEFAULT_BEAT_LABELS, SENTINEL_LEFT_CLICK]
            if (
                data.hasOwnProperty("kaiAnalysis") &&
                data.kaiAnalysis.beats &&
                data.kaiAnalysis.beats.length > 0
            ) {
                const beats = data.kaiAnalysis.beats
                annotations.beats = beats.map((beat) => {
                    return {i: beat.offset, t: "n"}
                })
            }
            annotations.audit.push({time: new Date().toISOString(),person: "system",message: "File opened"})

            // Rhythm Morphology Generation
            if (
                data.hasOwnProperty("kai12Analysis") &&
                data.kai12Analysis.hasOwnProperty("morphologyHighSpec") &&
                data.kai12Analysis.hasOwnProperty("morphologyHighSens") &&
                data.kai12Analysis.hasOwnProperty("rhythmHighSpec") &&
                data.kai12Analysis.hasOwnProperty("rhythmHighSens")
            ) {
                annotations.algStatement = {...annotations.algStatement, ...RhythmMorphologyGen(data.kai12Analysis)}
            }
            
            // CHECK IF ITS A TURK JSON 
            if (data.hasOwnProperty("annotations") ) {
                if (data.annotations.hasOwnProperty("beatsV2"))
                    annotations.beats = data.annotations.beatsV2
                if (data.annotations.hasOwnProperty("intervalsV2"))
                    annotations.intervals = data.annotations.intervalsV2
                annotations.audit = data.annotator


                if (data.annotations.hasOwnProperty("global"))
                    annotations.global = data.annotations.global
                if (data.annotations.hasOwnProperty("labels")) {
                    if (data.annotations.labels.hasOwnProperty("beats")) {
                        annotations.labels.beats = data.annotations.labels.beats
                    }
                    if (data.annotations.labels.hasOwnProperty("intervals")) {
                        annotations.labels.intervals = data.annotations.labels.intervals
                    }
                }
                if (data.annotations.hasOwnProperty("doctorNote")) {
                    annotations.doctorNote = data.annotations.doctorNote
                }

                applyMedianBeatFiducialPoints(data, annotations)

                // Handling the overread JSON files
                if (data.annotations.hasOwnProperty("overreadMode") && data.annotations.overreadMode === true) {
                    annotations.overreadMode = data.annotations.overreadMode
                    annotations.NeedFollowup = data.annotations.NeedFollowup
                    annotations.comment = data.annotations.comment
                    annotations.originalDx = data.annotations.originalDx
                    annotations.confirmedDx = data.annotations.confirmedDx
                }
            }

            return {
                original,
                filtered,
                annotations,
                error,
            }
		case "turk":
            // Check if the JSON is a valid 12L ECG
            if (!data.hasOwnProperty("samples")) {
                return {
                    original,
                    filtered,
                    annotations,
                    error: "Uploaded JSON is not a valid 12L ECG",
                }
            }
            let ecgTurkLeads = {}
            ecgTurkLeads.I = data.samples;
            if (data.hasOwnProperty("extraLeads")) {
                data.extraLeads.map((lead, index) => {
                    const leadName = lead.title.split(" ")[1];
                    ecgTurkLeads[leadName] = lead.samples;
                });
            }
            // Store the raw measured leads in original.samples before any
            // processing so that OnFiltered can re-filter from plain arrays.
            original.samples = {...ecgTurkLeads};
            if (ecgTurkLeads.I && ecgTurkLeads.II) {
                const filteredMeasured = defaultFilterECG(data.frequency, data.mainsFreq ?? 60, original.samples, original.gain);
                ecgTurkLeads = calculateLeads(filteredMeasured);
                filtered.filters.ButterworthHighPass = 0.5
                filtered.filters.ButterworthLowPass = 40
                filtered.filters.adaptive = true
            }

            original.frequency = data.frequency
            original.mainsFrequency = data.mainsFreq ?? 60
            original.gain = data.gain
            original.duration = data.duration/data.frequency
            original.info = {}

            filtered.frequency = data.frequency
            filtered.mainsFrequency = data.mainsFreq ?? 60
            filtered.gain = data.gain
            filtered.duration = data.duration/data.frequency
            filtered.samples = ecgTurkLeads
            filtered.info = {}

            // Read turk annotation
            
            if (data.hasOwnProperty("algorithmLabels")) 
                annotations.algDet = data.algorithmLabels

            if (data.hasOwnProperty("annotator")) 
                annotations.audit = data.annotator

            if (data.hasOwnProperty("annotations") && data.annotations.hasOwnProperty("global"))
                annotations.global = data.annotations.global

            if (data.hasOwnProperty("annotations") && data.annotations.hasOwnProperty("doctorNote"))
                annotations.doctorNote = data.annotations.doctorNote

            let intervalLabels = {}
            let intervalLabelsArray = []
            // Extract Labels from data
            if (data.hasOwnProperty("intervalLabels")) {
                data.intervalLabels.map((label, index) => {
                    intervalLabels[label] = index
                    intervalLabelsArray.push({id: index, name: label, color: "#00ff00", slug: label[0].toLowerCase()})
                })
                annotations.labels.intervals = intervalLabelsArray
            }

            let beatLabels = {}
            let beatLabelsArray = []
            // Extract Labels from data
            if (data.hasOwnProperty("legend") && data.legend.hasOwnProperty("beats")) {
                // data.legend.beats.map((beat, index) => {
                //     beatLabels[beat.label] = index
                //     beatLabelsArray.push({id: index, name: beat.name, color: beat.color, slug: beat.label})
                // })
                // annotations.labels.beats = beatLabelsArray

                annotations.labels.beats = [
                    {name: "DEFAULT_LEFT_CLICK", color: "#8c8c8c", slug: "ddf"},
                ]
                data.legend.beats.map((beatLegends, index) => {
                    annotations.labels.beats.push ({name: beatLegends.name, color: beatLegends.color, slug: beatLegends.label})
                })
                if(data.legend.beats.length === 0)

                annotations.labels.beats = [...DEFAULT_BEAT_LABELS, SENTINEL_LEFT_CLICK]
            }

            if (data.hasOwnProperty("annotations")) {
                // use beatsV2 as primary source of truth on beat annotations; use beatLabels as a fallback
                if (data.annotations.hasOwnProperty("beatsV2")) {
                    annotations.beats = data.annotations.beatsV2
                } else if (data.annotations.hasOwnProperty("beatLabels")) {
                    let beatFinalArray = []
                    data.annotations.beatLabels.map((beat, index) => {
                        beatFinalArray.push({i: beat.sample, t:beat.label})
                    })
                    // data.annotations.beatLabels.map((beat, index) => {
                    //     beatFinalArray.push([beat.sample, beatLabels[beat.label]])
                    // });
                    annotations.beats = beatFinalArray
                }
            }

            // TODO: Port Turk Intervals to Annotator
            if (data.hasOwnProperty("annotations") && data.annotations.hasOwnProperty("intervalsV2")) {
                annotations.intervals = data.annotations.intervalsV2
            }

            applyMedianBeatFiducialPoints(data, annotations)
            // if (data.hasOwnProperty("annotations") && data.annotations.hasOwnProperty("intervals")) {
            //     // To Do : Convert to [offset, beatType]
            //     annotations.intervals = data.annotations.intervals
            // }
            // "intervals": [
            //     {
            //         "end": 2007,
            //         "label": "Noisy",
            //         "start": 1142
            //     }
            // ],

			return {
				original,
				filtered,
				annotations,
				error,
			}
		default:
			return {
				original,
				filtered,
				annotations,
				error: "Universal Formatter: Invalid type",
			}
	}
}
